/**
 * R1 Intro Video Upload — section upload video giới thiệu đội ở Vòng Sơ loại.
 *
 * Theo Điều 9.3/9.4: video chính thức nộp ở R1, không phải đăng ký đội.
 * Dùng cho tie-break Điều 10.3 khi hoà điểm Vòng Sơ loại.
 *
 * Hiển thị cho đội thi (member của team) khi:
 *  - Team đã được phân vào case
 *  - Round đang ACTIVE (memorial)
 *
 * Logic upload tái sử dụng useUploadTeamIntroVideo + useUpdateTeam (gắn metadata
 * vào team.intro_video).
 */
import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { CloudUpload, Movie, Delete } from '@mui/icons-material'

import {
  useUploadTeamIntroVideoMutation,
  useUpdateTeamMutation,
  type Team,
} from '../store/api/teamsApi'

export interface R1IntroVideoUploadProps {
  team: Team
  /** Có phải member của team (true) hay BTC/judge view (false). */
  isOwnTeam: boolean
  /** Hiển thị compact (in dialog) vs full (in page). */
  compact?: boolean
  onUploaded?: () => void
}

const MAX_SIZE = 200 * 1024 * 1024 // 200MB

export function R1IntroVideoUpload({
  team,
  isOwnTeam,
  compact = false,
  onUploaded,
}: R1IntroVideoUploadProps) {
  const [uploadVideo, { isLoading: isUploading }] = useUploadTeamIntroVideoMutation()
  const [updateTeam, { isLoading: isUpdatingTeam }] = useUpdateTeamMutation()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const introVideo = team.intro_video
  const isLoading = isUploading || isUpdatingTeam

  const handleUpload = async (file: File) => {
    setError(null)
    setSuccess(null)
    if (file.size > MAX_SIZE) {
      setError(`Video vượt quá 200MB (${(file.size / 1024 / 1024).toFixed(1)}MB)`)
      return
    }
    try {
      const uploaded = await uploadVideo({ file }).unwrap()
      await updateTeam({
        id: team.id,
        intro_video: uploaded as any,
      }).unwrap()
      setSuccess(`Đã nộp video "${uploaded.file_name}"`)
      onUploaded?.()
    } catch (err: any) {
      setError(err?.data?.detail || err?.message || 'Upload thất bại')
    }
  }

  const handleRemove = async () => {
    if (!window.confirm('Xoá video giới thiệu hiện tại?')) return
    try {
      await updateTeam({
        id: team.id,
        intro_video: null as any,
      }).unwrap()
      setSuccess('Đã xoá video.')
      onUploaded?.()
    } catch (err: any) {
      setError(err?.data?.detail || err?.message || 'Xoá video thất bại')
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: compact ? 1.5 : 2,
        borderRadius: 2,
        borderColor: introVideo ? 'success.light' : 'warning.light',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
        <Movie fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>
          Video giới thiệu đội — Vòng Sơ loại
        </Typography>
        {introVideo ? (
          <Chip size="small" label="Đã nộp" color="success" variant="filled" />
        ) : (
          <Chip size="small" label="Chưa nộp" color="warning" variant="outlined" />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Theo Điều 9.3/9.4: video chính thức nộp ở R1. Dùng cho tie-break Điều 10.3
        khi đội cùng điểm tổng. Định dạng MP4/WebM/MOV, ≤200MB.
      </Typography>

      {introVideo && (
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap">
          <Chip
            size="small"
            label={`${introVideo.file_name}${
              introVideo.file_size
                ? ` · ${(Number(introVideo.file_size) / 1024 / 1024).toFixed(1)}MB`
                : ''
            }`}
            variant="outlined"
            color="success"
          />
          {introVideo.uploaded_at && (
            <Typography variant="caption" color="text.secondary">
              {new Date(introVideo.uploaded_at).toLocaleString('vi-VN')}
            </Typography>
          )}
        </Stack>
      )}

      {isOwnTeam && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            component="label"
            variant={introVideo ? 'outlined' : 'contained'}
            color="primary"
            size="small"
            startIcon={isLoading ? <CircularProgress size={14} /> : <CloudUpload />}
            disabled={isLoading}
            sx={{ textTransform: 'none' }}
          >
            {isLoading
              ? 'Đang tải...'
              : introVideo
                ? 'Thay video khác'
                : 'Tải lên video giới thiệu'}
            <input
              hidden
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
              onChange={async (e) => {
                const f = e.target.files?.[0]
                if (f) await handleUpload(f)
                e.target.value = ''
              }}
            />
          </Button>
          {introVideo && (
            <Button
              size="small"
              color="error"
              variant="text"
              startIcon={<Delete />}
              onClick={handleRemove}
              disabled={isLoading}
              sx={{ textTransform: 'none' }}
            >
              Xoá
            </Button>
          )}
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
    </Paper>
  )
}
